/**
 * HelpContentService — local cache for the help center corpus.
 *
 * Mirrors the sharedNoteService shape: fetch public content from the worker
 * (GET /public/help), persist it into SQLite as a single blob row, read it
 * synchronously for instant offline rendering, and refresh in the background
 * when the Help dialog opens. The worker's ETag (derived from the content
 * version) makes refreshes cheap: unchanged content comes back as a 304.
 *
 * Swift mirrors this in `HelpContentService.swift` + `HelpStore.swift`.
 */

import type { HelpPayload } from "@caliu/types";
import { resolveLocale } from "@caliu/utils";
import i18n from "../i18n";
import { Sentry } from "../sentry";
import { execute, notifyChanges, queryOne } from "../sqlite";

const API_URL = import.meta.env.VITE_API_URL ?? "https://api.caliuapp.com";

export interface CachedHelp {
  payload: HelpPayload;
  version: string;
  fetchedAt: string;
}

interface HelpContentRow {
  locale: string;
  version: string;
  etag: string | null;
  payload_json: string;
  fetched_at: string;
}

/** Read the cached help corpus, if any. Synchronous, works offline. */
export function getCachedHelp(): CachedHelp | null {
  const row = queryOne<HelpContentRow>(
    "SELECT locale, version, etag, payload_json, fetched_at FROM help_content WHERE id = 'current'",
  );
  if (!row) return null;
  try {
    return {
      payload: JSON.parse(row.payload_json) as HelpPayload,
      version: row.version,
      fetchedAt: row.fetched_at,
    };
  } catch (err) {
    // Corrupt cache row — drop it so the next refresh rebuilds it.
    Sentry.captureException(err);
    execute("DELETE FROM help_content WHERE id = 'current'");
    return null;
  }
}

/**
 * Refresh the cached corpus from the worker. Best-effort: a no-op offline,
 * silent on failure (the cache keeps serving), cheap when unchanged (304).
 * Uses raw fetch instead of apiFetch because apiFetch cannot distinguish a
 * 304 from an error, and this endpoint needs no credentials.
 */
const REFRESH_THROTTLE_MS = 30 * 60 * 1000; // 30 minutes

export async function refreshHelp(): Promise<void> {
  if (!navigator.onLine) return;

  const cached = queryOne<{
    etag: string | null;
    fetched_at: string | null;
    locale: string | null;
  }>("SELECT etag, fetched_at, locale FROM help_content WHERE id = 'current'");

  const language = i18n.resolvedLanguage || i18n.language || "en";

  // Help content changes rarely, and every entry point (Settings menu open,
  // Help dialog open) calls this. Skip the network entirely when the cache was
  // last checked under 30 minutes ago AND still holds the locale we'd request
  // now. A language switch resolves to a different bundle, so it bypasses the
  // throttle and revalidates immediately.
  if (cached?.fetched_at && cached.locale === resolveLocale([language])) {
    const age = Date.now() - new Date(cached.fetched_at).getTime();
    if (age >= 0 && age < REFRESH_THROTTLE_MS) return;
  }

  let res: Response;
  try {
    const headers: Record<string, string> = { "Accept-Language": language };
    // The ETag encodes the effective locale + content version, so this stays
    // correct across language switches: a language whose content resolves to
    // the same bundle revalidates as 304, a different bundle misses and
    // returns 200 with fresh content.
    if (cached?.etag) {
      headers["If-None-Match"] = cached.etag;
    }
    res = await fetch(`${API_URL}/public/help`, { headers });
  } catch (err) {
    // Network error — only unexpected if we believed we were online.
    if (navigator.onLine) Sentry.captureException(err);
    return;
  }

  const fetchedAt = new Date().toISOString();

  if (res.status === 304) {
    execute("UPDATE help_content SET fetched_at = ? WHERE id = 'current'", [fetchedAt]);
    return;
  }

  if (!res.ok) {
    Sentry.captureException(
      new Error(`refreshHelp: GET /public/help failed with ${res.status}`),
    );
    return;
  }

  try {
    const payload = (await res.json()) as HelpPayload;
    execute(
      `INSERT OR REPLACE INTO help_content (id, locale, version, etag, payload_json, fetched_at)
       VALUES ('current', ?, ?, ?, ?, ?)`,
      [
        // The worker reports the effective locale after fallback; keying the
        // cache by the requested language would refetch forever for locales
        // that fall back to English.
        payload.locale,
        payload.version,
        res.headers.get("ETag"),
        JSON.stringify(payload),
        fetchedAt,
      ],
    );
    notifyChanges(["help_content"]);
  } catch (err) {
    Sentry.captureException(err);
  }
}
